import { Button } from "@components/blocks/Button";
import NumberInput from "@components/common/hookform/NumberInput";
import { isValidationError } from "@justhome/common";
import {
  ClientMode,
  GetPartnerPropertyError,
  PartnerCRMApplication,
  PartnerCRMProperty,
} from "@justhome/common/api/core";
import { InlineNotification, Title } from "@justhome/react-ui";
import { FathomEvent } from "@lib/constants";
import { useApplicationTranslation } from "@lib/hooks/translations";
import ApplicationLayout from "@modules/application/components/ApplicationLayout";
import BenefitSection from "@modules/application/components/TransferWithPropertyV2/BenefitSection";
import PropertyError from "@modules/partner-application/components/PropertyError";
import { TFunction } from "i18next";
import { useRouter } from "next/router";
import { FC, useMemo } from "react";
import { useForm } from "react-hook-form";
import PropertyCard from "../PropertyCard";

export interface PartnerApplicationFormValue {
  equity: number;
  monthlyNetIncome: number;
}

type OfferState = "pre" | "post";

interface TransferTexts {
  title: string;
  description1: string;
  description2: string;
  description3: string;
  benefit_title?: string;
  primaryCta: string;
  secondaryCta?: string;
  secondaryLink?: string;
  benefits?: string[];
}

const getTextsForState = (
  translate: TFunction<"application">,
  offerState: OfferState,
): TransferTexts => {
  switch (offerState) {
    case "pre":
      return {
        title: translate("partner_transfer_pre_title"),
        description1: translate("partner_transfer_pre_description_1"),
        description2: translate("partner_transfer_description_2"),
        description3: translate("partner_transfer_description_3"),
        benefit_title: translate("partner_transfer_pre_benefit_title"),
        primaryCta: translate("partner_transfer_pre_cta_primary"),
        secondaryCta: translate("partner_transfer_pre_cta_secondary"),
        secondaryLink: "",
        benefits: [
          translate("partner_transfer_pre_benefit_1"),
          translate("partner_transfer_pre_benefit_2"),
          translate("partner_transfer_pre_benefit_3"),
          translate("partner_transfer_pre_benefit_4"),
          translate("partner_transfer_pre_benefit_5"),
        ],
      };
    case "post":
    default:
      return {
        title: translate("partner_transfer_post_title"),
        description1: translate("partner_transfer_post_description_1"),
        description2: translate("partner_transfer_description_2"),
        description3: translate("partner_transfer_description_3"),
        benefit_title: translate("partner_transfer_post_benefit_title"),
        primaryCta: translate("partner_transfer_post_cta_primary"),
        secondaryCta: translate("partner_transfer_post_cta_secondary"),
        secondaryLink: "/terminbuchung",
        benefits: [
          translate("partner_transfer_post_benefit_1"),
          translate("partner_transfer_post_benefit_2"),
          translate("partner_transfer_post_benefit_3"),
          translate("partner_transfer_post_benefit_4"),
          translate("partner_transfer_post_benefit_5"),
        ],
      };
  }
};

interface TransferWithPropertyProps {
  property: PartnerCRMProperty | undefined;
  propertyError?: GetPartnerPropertyError | null;
  onSubmit: (value: PartnerCRMApplication) => void;
  isLoading: boolean;
  isError: boolean;
  isSecondaryLoading: boolean;
  isSecondaryError: boolean;
  showTerms?: boolean;
  offerState: string;
  onSecondaryCTA: (value: PartnerCRMApplication) => void;
  defaultValues?: PartnerCRMApplication;
  showBenefits?: boolean;
  clientMode: ClientMode;
}

const TransferWithPropertyV2: FC<TransferWithPropertyProps> = ({
  property,
  propertyError,
  onSubmit,
  isError,
  isLoading,
  isSecondaryError,
  isSecondaryLoading,
  showTerms = true,
  offerState,
  onSecondaryCTA,
  defaultValues,
  showBenefits = true,
  clientMode,
}) => {
  const { query } = useRouter();
  const propertyId = query["property-id"] as string;
  const { t } = useApplicationTranslation();
  const { control, handleSubmit } = useForm<PartnerCRMApplication>({
    mode: "onBlur",
    defaultValues,
  });

  const transferTexts = useMemo(
    () => getTextsForState(t, offerState as OfferState),
    [t, offerState],
  );

  return (
    <ApplicationLayout>
      <div className="grid m:grid-cols-12 items-center py-3s">
        <div className="col-span-12 m:col-span-8 m:col-start-3 l:col-span-6 l:col-start-4">
          <Title as="h1" className="text-d3 mb-3s m:mb-m">
            {transferTexts.title}
          </Title>
          {!offerState && <p>Hallo,</p>}
          <p className="mb-3s">{transferTexts.description1}</p>

          <PropertyCard
            property={property}
            isLoading={!property}
            className="mb-3s m:mb-3m"
          />
          {isValidationError(propertyError) ? (
            <PropertyError error={propertyError} />
          ) : (
            <>
              {offerState !== "pre" && (
                <>
                  <p>{transferTexts.description2}</p>
                  <p className="mb-3s font-medium">
                    {transferTexts.description3}
                  </p>

                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="grid grid-cols-1 m:grid-cols-2 gap items-start"
                  >
                    <NumberInput
                      disabled={isLoading}
                      label={t("down_payment_label")}
                      name="equity"
                      control={control}
                      size="md"
                      suffix="€"
                      rules={{
                        required: {
                          value: true,
                          message: t("error_down_payment"),
                        },
                        min: 0,
                      }}
                      info={t("down_payment_info")}
                    />
                    <NumberInput
                      disabled={isLoading}
                      label={t("monthly_net_income_label")}
                      name="monthlyNetIncome"
                      control={control}
                      size="md"
                      suffix="€"
                      rules={{
                        required: {
                          value: true,
                          message: t("error_monthly_net_income"),
                        },
                        min: {
                          value: 1800,
                          message: t("error_monthly_net_income"),
                        },
                      }}
                      info={t("monthly_net_income_info")}
                    />
                    {isError && (
                      <InlineNotification
                        className="m:col-span-2 mt-2s mb-s"
                        type="error"
                      >
                        {t("generic_error")}
                      </InlineNotification>
                    )}
                    <Button
                      id={
                        clientMode === "PROPSTACK"
                          ? FathomEvent.CONTRACT_PROPSTACK_CALCULATOR
                          : FathomEvent.CONTRACT_ONOFFICE_CALCULATOR
                      }
                      trackingId={
                        clientMode === "PROPSTACK"
                          ? FathomEvent.CONTRACT_PROPSTACK_CALCULATOR
                          : FathomEvent.CONTRACT_ONOFFICE_CALCULATOR
                      }
                      variant="primary"
                      label={transferTexts.primaryCta}
                      type="submit"
                      className="!w-auto"
                      size="md"
                      isLoading={isLoading}
                      iconEnd="Right"
                    />
                  </form>
                </>
              )}
              {offerState === "pre" && showBenefits && (
                <BenefitSection
                  propertyId={propertyId}
                  trackingId={
                    clientMode === "PROPSTACK"
                      ? FathomEvent.CONTRACT_PROPSTACK_FINANCE_CHECK_CALL
                      : FathomEvent.CONTRACT_ONOFFICE_FINANCE_CHECK_CALL
                  }
                  benefits={transferTexts.benefits}
                  title={transferTexts.benefit_title ?? ""}
                  ctaText={transferTexts.secondaryCta ?? ""}
                  onSubmit={onSecondaryCTA}
                  isLoading={isSecondaryLoading}
                  isError={isSecondaryError}
                  errorLabel={t("generic_error")}
                />
              )}
              {showTerms && (
                <p
                  className="text-body-2 m:col-span-2 mt-3s"
                  dangerouslySetInnerHTML={{
                    __html: t("form_consent_terms_partner"),
                  }}
                />
              )}
              {offerState === "post" && showBenefits && (
                <>
                  <BenefitSection
                    propertyId={propertyId}
                    trackingId={
                      clientMode === "PROPSTACK"
                        ? FathomEvent.CONTRACT_PROPSTACK_CALCULATOR_CALL
                        : FathomEvent.CONTRACT_ONOFFICE_CALCULATOR_CALL
                    }
                    className="mt-2m"
                    benefits={transferTexts.benefits}
                    title={transferTexts.benefit_title ?? ""}
                    ctaText={transferTexts.secondaryCta ?? ""}
                    onSubmit={onSecondaryCTA}
                    isLoading={isSecondaryLoading}
                    isError={isSecondaryError}
                    errorLabel={t("generic_error")}
                    termsText={
                      showTerms ? t("form_consent_terms_partner") : undefined
                    }
                    hasBackground
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </ApplicationLayout>
  );
};

export default TransferWithPropertyV2;
